import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { getDisplayTextLabelText } from '@vista-digital/occ-domain';
import { observer } from 'mobx-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { createTextMaskInputElement } from 'text-mask-core';
import { useOccContext } from '../../../../../context/occ/hooks/use-occ-context';
import { Icon } from '../../../../icon';
import { defaultInputType } from '../types/type';
import { getInputMode } from '../utils/utils';
const bWrapper = bemClassBuilder('v-input-wrapper');
const b = bemClassBuilder('v-input');
/**
 * Renders an input component.
 */
const InputBase = (props, ref) => {
    var _a;
    const { assets: occAssets, config: occConfig } = useOccContext();
    const { enableFloatingInputLabels } = occConfig.form;
    const { mask, pipe, value, style, type, className, onChange, onChangeValue, onBlur, onFocus } = props;
    const inputElementRef = useRef(null);
    const textMaskInputElementRef = useRef();
    const [hasValue, setHasValue] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const showPlaceholder = () => {
        if (!enableFloatingInputLabels) {
            return true;
        }
        return isFocused || !props.children;
    };
    const placeholder = showPlaceholder() ? props.placeholder : undefined;
    useEffect(() => {
        setHasValue(Boolean(value));
    }, [value]);
    const initTextMask = useCallback(() => {
        if (!mask || !inputElementRef.current) {
            return;
        }
        if (!hasValue && enableFloatingInputLabels) {
            return;
        }
        textMaskInputElementRef.current = createTextMaskInputElement({
            inputElement: inputElementRef.current,
            mask,
            pipe,
            keepCharPositions: true,
            placeholderChar: '\u2000'
        });
        textMaskInputElementRef.current.update(value.toString());
    }, [mask, pipe, value, hasValue, enableFloatingInputLabels]);
    useEffect(() => {
        if (mask) {
            initTextMask();
        }
    }, [initTextMask, mask, pipe, value]);
    useImperativeHandle(ref, () => ({
        focus: () => {
            var _a;
            (_a = inputElementRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        },
        blur: () => {
            var _a;
            (_a = inputElementRef.current) === null || _a === void 0 ? void 0 : _a.blur();
        }
    }));
    const handleChange = useCallback(event => {
        var _a;
        (_a = textMaskInputElementRef.current) === null || _a === void 0 ? void 0 : _a.update();
        onChange === null || onChange === void 0 ? void 0 : onChange(event);
        onChangeValue === null || onChangeValue === void 0 ? void 0 : onChangeValue(event.target.value);
    }, [onChange, onChangeValue]);
    const handleOnFocus = useCallback(event => {
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(event);
        setIsFocused(true);
    }, [onFocus, setIsFocused]);
    const handleOnBlur = useCallback(event => {
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
        setHasValue(!!event.target.value);
        setIsFocused(false);
    }, [onBlur]);
    const inputMode = (_a = props.inputMode) !== null && _a !== void 0 ? _a : getInputMode(type);
    return (_jsxs("div", { className: bWrapper.block({ hasValue, focused: isFocused }), children: [props.children, _jsx("input", Object.assign({ className: b.block({ style }, className) }, props.ariaAttributes, { type: type !== null && type !== void 0 ? type : defaultInputType, inputMode: inputMode, id: props.id, name: props.name, value: value, placeholder: placeholder || undefined, disabled: props.disabled, "aria-describedby": props.helpTextId, "aria-label": props.label ? getDisplayTextLabelText(props.label) : undefined, onBlur: handleOnBlur, onFocus: handleOnFocus, onChange: handleChange, onKeyPress: props.onKeyPress, onKeyDown: props.onKeyDown, onWheel: props.onWheel, maxLength: props.maxLength, ref: inputElementRef, autoComplete: props.autoCompleteType })), !!props.enableValidatingIndicator && !!props.isValidating && (_jsx("div", { className: b.element('validating-icon'), children: _jsx(Icon, { name: props.validatingIconName || occAssets.loadingIconName }) }))] }));
};
export const InputUI = observer(forwardRef(InputBase));
InputUI.displayName = 'InputUI';
//# sourceMappingURL=ui.js.map